import { useEffect, useState } from "react"
import apiClient from "../../../Utils/apiClient"
import {
  Button,
  Dropdown,
  Modal,
  message,
  Tag,
  notification,
  AutoComplete,
} from "antd"
import { useNavigate } from "react-router-dom"
import PanelLayout from "../../../Layout/PanelLayout"
import TableBox from "../../../components/TableBox/TableBox"
import "./GroupDevices.scss"
import { DeleteOutlined, MoreOutlined } from "@ant-design/icons"
import ExportTableData from "../../../components/ExportTableData/ExportTableData"
import FilterDrawer from "../../../components/FilterDrawer/FilterDrawer"
import { getStoredPageSize } from "../../../Utils/TableUtils/TableUtils"
import FetchIpLocationButton from "../../../components/FetchIpLocationButton/FetchIpLocationButton"

const GroupDevicesList = () => {
  const page = `Group Device List`
  const modalPage = `Manage Group Devices`
  const currentUrl = window.location.href
  const userId = currentUrl.split("/").pop()
  const navigate = useNavigate()
  const [data, setData] = useState([])
  const [isOpenModal, setIsOpenModal] = useState(false)
  const [render, setRender] = useState(false)
  const [devices, setDevices] = useState([])
  const [selectedDevices, setSelectedDevices] = useState([])
  const [selectedDevicesForRemove, setSelectedDevicesForRemove] = useState([])
  const [isShowRemoveCheckBox, setIsShowRemoveCheckBox] = useState(false)
  const [searchValue, setSearchValue] = useState("")
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [filteredDevices, setFilteredDevices] = useState([])
  const [api, contextHolder] = notification.useNotification()
  const [filters, setFilters] = useState({
    hostname: "",
    user: "",
    devdeviceStatus: "",
  })
  const [paginationData, setPaginationData] = useState({
    page: 1,
    pageSize: getStoredPageSize(page) || 10,
    total: 0,
  })
  const [modalPaginationData, setModalPaginationData] = useState({
    page: 1,
    pageSize: getStoredPageSize(modalPage) || 10,
    total: 0,
  })

  const getdata = async (
    customPage = paginationData.page,
    customPageSize = paginationData.pageSize
  ) => {
    try {
      const params = {
        page: customPage,
        pageSize: customPageSize,
      }
      const result = await apiClient(
        `device-grouping/getGroupById/${userId}`,
        "GET",
        null,
        { params }
      )
      if (result && result.success) {
        setData(result.group)
        setFilteredDevices(result.devices?.data)
        setPaginationData(result.devices?.pageData)
      }
    } catch (err) {
      message.error("Filted to fetch data!")
    } finally {
    }
  }

  useEffect(() => {
    getdata(1)
    handleClearFilter()
  }, [render])

  const handleFilter = async (
    customPage = paginationData.page,
    customPageSize = paginationData.pageSize
  ) => {
    try {
      const params = {
        ...filters,
        page: customPage,
        pageSize: customPageSize,
      }
      const res = await apiClient(
        `device-grouping/getGroupById/${userId}`,
        "GET",
        null,
        { params }
      )

      if (res && res?.success) {
        setFilteredDevices(res.devices?.data)
        setPaginationData(res.devices?.pageData)
      } else {
        setFilteredDevices([])
      }
    } catch (err) {
      console.error("Filter failed", err)
    } finally {
      setDrawerOpen(false)
      setSelectedDevicesForRemove([])
      setIsShowRemoveCheckBox(false)
    }
  }

  const handleClearFilter = () => {
    setFilters({
      hostname: "",
      user: "",
      deviceStatus: "",
    })
    getdata(1, paginationData.pageSize)
    setDrawerOpen(false)
    setSelectedDevicesForRemove([])
    setIsShowRemoveCheckBox(false)
  }

  const handleTableChange = (pagination) => {
    const { current, pageSize } = pagination
    setPaginationData((prev) => ({ ...prev, page: current, pageSize }))

    const hasFilters = Object.values(filters).some((val) => val.trim() !== "")

    if (hasFilters) {
      handleFilter(current, pageSize)
    } else {
      getdata(current, pageSize)
    }
  }

  const handleGetDevices = async (
    customPage = modalPaginationData.page,
    customPageSize = modalPaginationData.pageSize
  ) => {
    setIsShowRemoveCheckBox(false)
    setSelectedDevices([])
    const params = {
      page: customPage,
      pageSize: customPageSize,
      ungrouped: true,
    }
    const res = await apiClient("devices", "GET", null, { params })

    if (res) {
      setDevices(res.data)
      setModalPaginationData(res.pageData)
      setIsOpenModal(true)
    } else {
      setDevices([])
    }
  }

  const handleSearchDevices = async (
    value = searchValue,
    customPage = modalPaginationData.page,
    customPageSize = modalPaginationData.pageSize
  ) => {
    setSearchValue(value)
    if (!value) {
      handleGetDevices()
      return
    }
    const params = {
      page: customPage,
      pageSize: customPageSize,
      ungrouped: true,
      q: value,
    }
    const result = await apiClient(`search`, "GET", null, { params })
    if (result && result.success) {
      setDevices(result.result?.data)
      setModalPaginationData(result.result?.pageData)
    } else {
      setDevices([])
    }
  }

  const onSearchInputChange = (value) => {
    setSearchValue(value)
    if (!value) {
      handleGetDevices()
    } else {
      handleSearchDevices(value, 1)
    }
  }

  const handleModalTableChange = (pagination) => {
    const { current, pageSize } = pagination
    setModalPaginationData((prev) => ({ ...prev, page: current, pageSize }))
    if (searchValue) {
      handleSearchDevices(searchValue, current, pageSize)
    } else {
      handleGetDevices(current, pageSize)
    }
  }

  const handleDeviceClick = (device) => {
    navigate(`/device-management/${device._id}`)
  }

  const handleAddDevice = async () => {
    const devices = selectedDevices.map((device) => device._id)
    const Group = await apiClient(
      `device-grouping/add-devices/${data?._id}`,
      "POST",
      { devices }
    )

    if (Group.success) {
      api.success({
        message: `Device${
          selectedDevices.length > 1 ? "s" : ""
        } Added Successfully`,
        description: (
          <div>
            {selectedDevices.length == 1 ? (
              <p>
                Device "<b>{selectedDevices[0].hostname}</b> –{" "}
                {selectedDevices[0].user || "Unknown User"}" has been
                successfully added to {data.name} Team group.
              </p>
            ) : (
              <>
                <p>
                  The following devices have been successfully added to{" "}
                  {data.name} Team group.
                </p>
                <ul style={{ paddingLeft: 20 }}>
                  {selectedDevices.map((device) => (
                    <li key={device._id}>
                      <b>{device.hostname}</b> – {device.user || "Unknown User"}
                    </li>
                  ))}
                </ul>
              </>
            )}
          </div>
        ),
        placement: "topRight",
        duration: 5,
      })

      setRender(!render)
      setSelectedDevices([])
      setDevices([])
      setIsOpenModal(false)
    }
  }

  const handleRemoveDevices = async (deviceIds = selectedDevicesForRemove) => {
    if (!deviceIds.length) return

    const selected = filteredDevices.filter((d) => deviceIds.includes(d._id))

    Modal.confirm({
      className: "remove-devices-confirm-modal",
      title: "Confirm Device Removal",
      content: (
        <div>
          <p>
            Are you sure you want to remove the following device
            {selected.length > 1 ? "s" : ""} from the <b>{data.name}</b> group?
          </p>
          <ul style={{ paddingLeft: 20 }}>
            {selected.map((device) => (
              <li key={device._id}>
                <b>{device.hostname}</b> – {device.user || "Unknown User"}
              </li>
            ))}
          </ul>
        </div>
      ),
      okText: "Yes, Remove Device",
      cancelText: "Cancel",
      centered: true,
      onOk: async () => {
        const removeDevices = await apiClient(
          `device-grouping/delete-group-devices/${data?._id}`,
          "POST",
          { devices: deviceIds }
        )

        if (removeDevices.success) {
          api.success({
            message: `Device${
              selected.length > 1 ? "s" : ""
            } Removed Successfully`,
            description: (
              <div>
                {selected.length == 1 ? (
                  <p>
                    Device "<b>{selected[0].hostname}</b> –{" "}
                    {selected[0].user || "Unknown User"}" has been successfully
                    removed from {data.name} Team group.
                  </p>
                ) : (
                  <>
                    <p>
                      The following devices have been successfully removed from{" "}
                      {data.name} Team group.
                    </p>
                    <ul style={{ paddingLeft: 20 }}>
                      {selected.map((device) => (
                        <li key={device._id}>
                          <b>{device.hostname}</b> –{" "}
                          {device.user || "Unknown User"}
                        </li>
                      ))}
                    </ul>
                  </>
                )}
              </div>
            ),
            placement: "topRight",
            duration: 5,
          })

          setRender(!render)
          setSelectedDevicesForRemove([])
          setIsShowRemoveCheckBox(false)
        } else {
          message.error("Failed to remove devices")
        }
      },
    })
  }

  const handleCloseModal = () => {
    setSearchValue("")
    setIsOpenModal(false)
    setSelectedDevices([])
    setDevices([])
  }

  const columns = [
    {
      title: "Hostname",
      dataIndex: "hostname",
      render: (text, record) => (
        <span
          style={{ color: "#1677ff", cursor: "pointer" }}
          onClick={() => handleDeviceClick(record)}
        >
          {text}
        </span>
      ),
    },
    {
      title: "User",
      dataIndex: "user",
    },
    {
      title: "IP",
      dataIndex: "ip",
      render: (ip, record) => (
        <span>
          {ip || "—"}
          <FetchIpLocationButton
            device={record}
            fields={["ip"]}
            onUpdated={(patch) =>
              setFilteredDevices((prev) =>
                (prev || []).map((row) =>
                  String(row._id) === String(patch._id)
                    ? { ...row, ...patch }
                    : row
                )
              )
            }
          />
        </span>
      ),
    },
    {
      title: "CPU (%)",
      dataIndex: "cpu",
      render: (cpu) => `${cpu}%`,
    },
    {
      title: "RAM (%)",
      dataIndex: "ram",
      render: (ram) => `${ram}%`,
    },
    {
      title: "Status",
      dataIndex: "deviceStatus",
      render: (status) => (
        <Tag color={status === "Online" ? "green" : "volcano"}>{status}</Tag>
      ),
    },
    {
      title: "Last Patch",
      dataIndex: "lastPatchDate",
      render: (date) => date || "N/A",
    },
    {
      title: "Last Sync",
      dataIndex: "lastSyncTime",
      render: (time) => time || "N/A",
    },
    {
      title: "Action",
      key: "action",
      render: (_, record) => {
        return (
          <Dropdown
            menu={{
              items: [
                {
                  key: "apps",
                  label: "App Management",
                  onClick: () => navigate(`/app-management/${record._id}`),
                },
                {
                  key: "patch",
                  label: "Patch Management",
                  onClick: () => navigate(`/patch-management/${record._id}`),
                },
                {
                  key: "delete",
                  label: "Remove Device",
                  onClick: () => handleRemoveDevices([record._id]),
                },
              ],
            }}
            trigger={["click"]}
          >
            <Button icon={<MoreOutlined />} />
          </Dropdown>
        )
      },
    },
  ]

  const filterFields = [
    {
      label: "Hostname",
      name: "hostname",
      endPoint: "users",
      placeholder: "Search Host Name",
    },
    {
      label: "User Name",
      name: "user",
      endPoint: "users",
      placeholder: "Search User Name",
    },
    {
      label: "Device Status",
      name: "deviceStatus",
      endPoint: "users",
      placeholder: "Search Device Status",
    },
  ]

  const modalColumns = columns
    .filter((col) =>
      ["hostname", "user", "ip", "deviceStatus"].includes(col.dataIndex)
    )
    .map(({ title, dataIndex }) => ({ title, dataIndex }))

  const exportColumns = columns
    .filter((col) => !["action"].includes(col.key))
    .map(({ title, dataIndex }) => ({ title, dataIndex }))

  return (
    <PanelLayout
      title={`Device List for ${data.name}`}
      subtitle="Members of this group and bulk actions you can run on them."
    >
      {contextHolder}
      <div className="device-group">
        <div className="page-toolbar page-toolbar--split">
          <div>
            <Button type="primary" onClick={(e) => handleGetDevices(1)}>
              Add Devices
            </Button>
          </div>
          <div className="d-flex gap-2 align-items-center flex-wrap">
            <FilterDrawer
              filters={filters}
              setFilters={setFilters}
              onApply={handleFilter}
              onClear={handleClearFilter}
              drawerOpen={drawerOpen}
              setDrawerOpen={setDrawerOpen}
              fields={filterFields}
            />
            <ExportTableData
              columns={exportColumns}
              dataSource={filteredDevices}
              fileName={`deviceList_${data.name}`}
              pdfTitle={`Devices mapped to ${data.name} Team Group`}
              sheetName={`${data.name}`}
              fontSize={8}
              titleFontSize={12}
            />
            <Button
              size="small"
              type="default"
              danger
              onClick={() => {
                if (isShowRemoveCheckBox) {
                  setSelectedDevicesForRemove([])
                } else {
                  message.info("Select the devices to be removed")
                }
                setIsShowRemoveCheckBox(!isShowRemoveCheckBox)
              }}
            >
              {isShowRemoveCheckBox ? "Cancel" : <DeleteOutlined />}
            </Button>

            {isShowRemoveCheckBox && selectedDevicesForRemove.length > 0 && (
              <Button
                size="small"
                type="primary"
                danger
                onClick={() => handleRemoveDevices()}
              >
                <DeleteOutlined /> ({selectedDevicesForRemove.length})
              </Button>
            )}
          </div>
        </div>

        <TableBox
          page={page}
          columns={columns}
          data={filteredDevices}
          rowSelection={
            isShowRemoveCheckBox
              ? {
                  selectedRowKeys: selectedDevicesForRemove,
                  onChange: (selectedRowKeys) =>
                    setSelectedDevicesForRemove(selectedRowKeys),
                  getCheckboxProps: () => ({
                    style: { cursor: "pointer" },
                  }),
                }
              : undefined
          }
          pagination={{
            current: paginationData.page,
            pageSize: paginationData.pageSize,
            total: paginationData.total,
            showSizeChanger: true,
          }}
          onChange={handleTableChange}
        />

        <Modal
          wrapClassName="devices-modal"
          open={isOpenModal}
          title={`Manage Devices for ${data?.name}`}
          onCancel={handleCloseModal}
          footer={[
            <Button key="submit" type="primary" onClick={handleAddDevice}>
              Add Selected Devices
            </Button>,
          ]}
        >
          <AutoComplete
            onChange={onSearchInputChange}
            value={searchValue}
            className="mb-3 w-75"
            placeholder="Search Hostname, User,Status"
          />
          <TableBox
            page={modalPage}
            columns={modalColumns}
            data={devices}
            rowKey={(record) => record._id}
            scroll={{ x: true }}
            rowSelection={{
              selectedRowKeys: selectedDevices.map((d) => d._id),
              onChange: (selectedRowKeys, selectedRows) => {
                setSelectedDevices(selectedRows)
              },
            }}
            shadow={false}
            pagination={{
              current: modalPaginationData.page,
              pageSize: modalPaginationData.pageSize,
              total: modalPaginationData.total,
              showSizeChanger: true,
            }}
            onChange={handleModalTableChange}
          />
        </Modal>
      </div>
    </PanelLayout>
  )
}

export default GroupDevicesList
